import { Guid, IDictionary, isNullOrEmpty } from "@ioip/core/lib";
import { createRuleEngine } from "../../flow/types/RuleEngine";
import { PermissionLevel } from "../../studio-types";
import { ControlType } from "../../types/ControlType";
import { IFieldSchema } from "./IFieldSchema";
import { FieldValueType, IFieldValue } from "./IFieldValue";
import CField from "./CField";
import { CForm } from "./JSForm";

export interface IJSControlProps {
    form: CForm;
    schema: IFieldSchema;
}

export default class CControl extends CField {

    #attributeId: Guid = null;
    #controlProps: IJSControlProps;
    #errorMessage: string;
    #parentId: Guid = null;
    #value: IFieldValue;
    #visibleRule: () => boolean;
    #onInvokeRender: () => void;

    constructor(props: IJSControlProps) {
        super(props.schema);
        this.#controlProps = props;
        this.#parentId = Guid.parse(this.#controlProps.schema.parentId);
        if (!isNullOrEmpty(this.#controlProps.schema.attributeId))
            this.#attributeId = Guid.parse(this.#controlProps.schema.attributeId);
        this.__initialize();
    }

    get parentId(): Guid {
        return this.#parentId;
    }

    get attributeId(): Guid {
        return this.#attributeId;
    }

    get colSm(): number {
        return this.#controlProps.schema.colSm ?? 12;
    }

    get colMd(): number {
        return this.#controlProps.schema.colMd ?? 12;
    }

    get colLg(): number {
        return this.#controlProps.schema.colLg ?? 12;
    }

    get colXl(): number {
        return this.#controlProps.schema.colXl ?? 12;
    }

    get colXxl(): number {
        return this.#controlProps.schema.colXxl ?? 12;
    }

    get colXxxl(): number {
        return this.#controlProps.schema.colXxxl ?? 12;
    }

    get name(): string {
        return this.#controlProps.schema.name;
    }

    get required(): boolean {
        return this.#controlProps.schema.required;
    }

    get help(): string {
        return this.#controlProps.schema.help;
    }

    get permissionLevel(): PermissionLevel {
        return this.#controlProps.schema.permissionLevel || PermissionLevel.none;
    }

    get form(): CForm {
        return this.#controlProps.form;
    }

    get disabled(): boolean {
        return this.#controlProps.schema.disabled;
    }

    get readonly(): boolean {

        // If the form is readonly, the control is readonly
        if (this.form.readonly)
            return true;

        // If the control has a readonly rule, return the result of the rule

        // Otherwise, return the value of the readonly property
        return this.#controlProps.schema.readonly;

    }

    get visible(): boolean {

        // If there is a rule for the visibility of the control, return the result of the rule
        if (this.#visibleRule != null) {
            return this.#visibleRule();
        }

        // Otherwise, return the value of the visible property
        return typeof this.#controlProps.schema.visible === 'boolean' ? this.#controlProps.schema.visible : true;

    }

    get value(): FieldValueType | IDictionary<IFieldValue>[] {
        return this.#controlProps.schema.controlType === ControlType.dataTable ? this.#value?.children ?? [] : this.#value?.value;
    }

    set value(value: FieldValueType | IDictionary<IFieldValue>[]) {
        const oldValue = this.#value;
        const controlType = this.controlType;
        if (oldValue != value) {
            if (controlType == ControlType.dataTable) {
                this.#value = { children: value as IDictionary<any>[] };
            } else {
                this.#value = { value: value as string | number | boolean };
            }
            this.#controlProps.form.refresh();
        }
    }

    get errorMessage(): any {
        return this.#errorMessage || "";
    }
    set errorMessage(value: any) {
        this.#errorMessage = value || "";
    }

    validate(): boolean {
        return true;
    }

    private __initialize() {

        // Initialize the visible rule
        const visibleRule = this.#controlProps.schema.visibleRule;
        if (visibleRule == null)
            return;
        const nodes = visibleRule.nodes;
        const edges = visibleRule.edges;
        this.#visibleRule = createRuleEngine<boolean>(this.name, nodes, edges, this.form);
    }

    set onInvokeRender(value: () => void) {
        this.#onInvokeRender = value;
    }

}